import type {
  BackgroundMessage,
  BackgroundResponse,
  AdCreative,
  TrackingEvent,
  SDKConfig,
  SessionData,
} from './types';
import { MessageType, ConfigError, TrackingEventType } from './types';
import {
  DEFAULT_BASE_URL,
  AD_REQUEST_ENDPOINT,
  TRACK_IMPRESSION_ENDPOINT,
  REQUEST_TIMEOUT,
  generateUUID,
} from './utils';
import { logger, setDebugMode } from './logger';

const TRACK_CLICK_ENDPOINT = '/api/ads/track-click';
const TRACK_AVAILABLE_ENDPOINT = '/api/ads/track-available';

// Global config stored in background worker
let globalConfig: SDKConfig | null = null;

// Guard to ensure the onMessage listener is registered at most once per worker lifetime
let listenerRegistered = false;

// Constants for session management
const SESSION_STORAGE_KEY = 'playaYieldSession';
const DEFAULT_MAX_IMPRESSIONS = 3;
const DEFAULT_SESSION_WINDOW_MS = 30 * 60 * 1000;

// Module-level in-memory session cache (persists across calls within same service worker)
let inMemorySession: SessionData | null = null;

// In-memory tracking of active refreshable ads (cleared on service worker restart)
const activeRefreshableAds = new Map<
  string,
  { timerId: ReturnType<typeof setInterval> }
>();

/**
 * Initialize PlayaYield SDK in the background service worker
 * This sets up the message handler and stores your configuration globally
 *
 * Call this once in your background service worker with your API key and settings.
 * Content scripts and popups can then use createAd() without needing to initialize.
 *
 * @param config SDK configuration including API key
 * @throws {ConfigError} If configuration is invalid
 *
 * @example
 * ```ts
 * // background.ts
 * import { initializePlayaYield } from '@playanext/playa-yield-sdk';
 *
 * initializePlayaYield({
 *   apiKey: 'your-api-key'
 * });
 * ```
 */
export function initializePlayaYield(config: SDKConfig): void {
  // Validate config
  if (!config.apiKey) {
    throw new ConfigError('apiKey is required');
  }

  // Store globally
  globalConfig = {
    ...config,
  };

  setDebugMode(globalConfig.debug ?? false);

  // Register the message listener exactly once per service worker lifetime.
  // If the worker restarts, the entire module re-executes and the flag resets
  // to false, so a fresh listener is registered. But if initializePlayaYield()
  // is called multiple times within the same lifetime (e.g. hot-reload, or
  // accidental double-call), we skip re-registration to avoid duplicate handlers.
  if (!listenerRegistered) {
    chrome.runtime.onMessage.addListener(
      (
        message: any,
        sender: chrome.runtime.MessageSender,
        sendResponse: (response: BackgroundResponse) => void
      ) => {
        // Only handle PlayaYield messages - check if message has expected structure
        if (!isPlayaYieldMessage(message)) {
          return false; // Let other listeners handle this message
        }

        (async () => {
          try {
            // Handle async operations for PlayaYield messages
            const data = await handleMessage(message, sender);
            sendResponse({ success: true, data });
          } catch (error: unknown) {
            logger.error('Unhandled background message error:', error);
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            sendResponse({
              success: false,
              error: errorMessage || 'Unknown error',
            });
          }
        })();

        // IMPORTANT (MV3): must return synchronously to keep the message channel open
        return true;
      }
    );

    // AFTER initializePlayaYield() is automatically wrapped to skip SDK messages.
    // This means developers don't need to guard their own listeners manually.
    const originalAddListener = chrome.runtime.onMessage.addListener.bind(
      chrome.runtime.onMessage
    );
    chrome.runtime.onMessage.addListener = (listener: any) => {
      const wrapped = (
        message: any,
        sender: chrome.runtime.MessageSender,
        sendResponse: (response: any) => void
      ) => {
        // If this is a PlayaYield message, skip — our listener above handles it
        if (isPlayaYieldMessage(message)) {
          return false;
        }
        return listener(message, sender, sendResponse);
      };
      originalAddListener(wrapped);
    };

    listenerRegistered = true;
  }

  logger.log('SDK initialized in background', {
    debug: globalConfig.debug ?? false,
  });
}

/**
 * Returns the active SDK configuration set via {@link initializePlayaYield}.
 *
 * @throws {ConfigError} when the SDK has not been initialized in the background.
 */
function getConfig(): SDKConfig {
  if (!globalConfig) {
    throw new ConfigError(
      'SDK not initialized. Call initializePlayaYield() in your background script first.'
    );
  }
  return globalConfig;
}

/**
 * Type guard to confirm a runtime message belongs to the PlayaYield SDK.
 *
 * @param message Arbitrary message received by the background listener.
 * @returns True when the message type matches a known {@link MessageType}.
 */
function isPlayaYieldMessage(message: any): message is BackgroundMessage {
  return (
    message &&
    typeof message === 'object' &&
    typeof message.type === 'string' &&
    Object.values(MessageType).includes(message.type as MessageType)
  );
}

/**
 * Dispatches background requests based on {@link MessageType}.
 *
 * @param message The parsed PlayaYield background message.
 * @param sender The Chrome runtime sender metadata.
 * @returns A message-specific payload or void, wrapped by the onMessage responder.
 */
async function handleMessage(
  message: BackgroundMessage,
  sender: chrome.runtime.MessageSender
): Promise<any> {
  switch (message.type) {
    case MessageType.AD_REQUEST:
      return handleAdRequest(message.payload);

    case MessageType.TRACK_EVENT:
      return handleTrackEvent(message.payload);

    case MessageType.TRACK_AVAILABLE:
      return handleTrackAvailable(message.payload);

    case MessageType.GET_CONFIG:
      return getConfig();

    case MessageType.PING:
      return { pong: true };

    case MessageType.GET_SESSION_DATA:
      return getSessionData();

    case MessageType.UPDATE_SESSION_IMPRESSION:
      return updateSessionImpression(message.payload);

    case MessageType.STOP_REFRESHABLE_AD:
      return stopRefreshableAd(message.payload);

    default:
      throw new Error(`Unknown message type: ${message.type}`);
  }
}

/**
 * Issues an ad request to the backend on behalf of a content/UI script.
 *
 * @param payload.options Ad request options (placement, size, metadata, etc.).
 * @returns Resolved {@link AdCreative} from the backend.
 * @throws Error when the HTTP request fails or times out.
 */
async function handleAdRequest(payload: { options: any }): Promise<AdCreative> {
  const { options } = payload;
  const config = getConfig();
  const baseUrl = config.baseUrl || DEFAULT_BASE_URL;

  // Build query params - apiKey goes in query to avoid CORS preflight
  const params = new URLSearchParams({
    apiKey: config.apiKey,
    extensionId: chrome.runtime.id,
    placement: options.placement,
    width: String(options.size?.width ?? options.width ?? 300),
    height: String(options.size?.height ?? options.height ?? 250),
  });

  // Add optional categories
  if (options.categories?.length) {
    params.set('categories', options.categories.join(','));
  }

  // Add metadata as URL-encoded JSON
  const metadata = { ...options };
  delete metadata.placement;
  delete metadata.size;
  delete metadata.categories;
  if (Object.keys(metadata).length > 0) {
    params.set('metadata', encodeURIComponent(JSON.stringify(metadata)));
  }

  const url = new URL(AD_REQUEST_ENDPOINT, baseUrl);
  url.search = params.toString();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Ad request failed: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    clearTimeout(timeoutId);
    throw new Error(`Ad request failed: ${error.message}`);
  }
}

/**
 * Tracks supported events (currently impressions) server-side.
 *
 * @param payload.event Tracking event containing impression metadata.
 * @remarks Clicks are handled client-side via navigation; failures are logged but not thrown.
 */
async function handleTrackEvent(payload: {
  event: TrackingEvent;
}): Promise<void> {
  const { event } = payload;
  const config = getConfig();
  const baseUrl = config.baseUrl || DEFAULT_BASE_URL;

  if (event.type === TrackingEventType.IMPRESSION) {
    const url = new URL(TRACK_IMPRESSION_ENDPOINT, baseUrl);

    // Extract metadata from event
    const { provider, viewHash, impressionHash } = event.metadata || {};

    // Don't track if no provider (shouldn't happen for valid ads)
    if (!provider) {
      logger.warn('Missing provider in impression event', event);
      return;
    }

    try {
      const body = {
        impressionId: event.impressionId,
        provider,
        viewHash,
        impressionHash,
      };

      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      logger.log('Impression tracked successfully', {
        impressionId: event.impressionId,
        provider,
      });
    } catch (error) {
      logger.error('Failed to track impression:', error);
      // We don't throw here to avoid disrupting the extension flow
    }

    return;
  }

  if (event.type === TrackingEventType.CLICK) {
    const url = new URL(TRACK_CLICK_ENDPOINT, baseUrl);

    const { provider, clickUrl, referrer } = event.metadata || {};

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          impressionId: event.impressionId,
          provider,
          clickUrl,
          referrer,
          timestamp: event.timestamp,
        }),
      });
    } catch (error) {
      logger.error('Failed to track click:', error);
    }
  }
}

/**
 * Tracks available events (ad rendered to DOM) server-side.
 * This is Taboola-specific and fires before the ad becomes visible.
 *
 * @param payload Available event data including impressionId, provider, and availableHash.
 */
async function handleTrackAvailable(payload: {
  impressionId: string;
  provider: string;
  availableHash?: string;
}): Promise<void> {
  const config = getConfig();
  const baseUrl = config.baseUrl || DEFAULT_BASE_URL;
  const url = new URL(TRACK_AVAILABLE_ENDPOINT, baseUrl);

  if (!payload.provider) {
    logger.warn('Missing provider in available event', payload);
    return;
  }

  try {
    const body = {
      impressionId: payload.impressionId,
      provider: payload.provider,
      availableHash: payload.availableHash,
    };

    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    logger.log('Available event tracked successfully', {
      impressionId: payload.impressionId,
      provider: payload.provider,
    });
  } catch (error) {
    logger.error('Failed to track available event:', error);
  }
}

/**
 * Loads or initializes per-session data stored in `chrome.storage.session` with an
 * in-memory fallback. Sessions are cleared when the browser closes or the service
 * worker stops.
 *
 * @returns The current {@link SessionData} (cached for reuse).
 */
async function getSessionData(): Promise<SessionData> {
  // Check in-memory cache first (persists across calls when storage unavailable)
  if (inMemorySession) {
    logger.log('Session loaded from in-memory cache', {
      impressionCount: inMemorySession.impressionCount,
      sessionId: inMemorySession.sessionId,
    });
    return inMemorySession;
  }

  // Check if chrome.storage.session is available
  if (chrome.storage?.session) {
    const result = await chrome.storage.session.get(SESSION_STORAGE_KEY);
    if (result[SESSION_STORAGE_KEY]) {
      logger.log('Session loaded from storage', {
        impressionCount: result[SESSION_STORAGE_KEY].impressionCount,
        sessionId: result[SESSION_STORAGE_KEY].sessionId,
      });
      // Cache in memory too
      inMemorySession = result[SESSION_STORAGE_KEY] as SessionData;
      return inMemorySession;
    }
  } else {
    // Fallback: session storage not available, use in-memory
    logger.warn('Session storage not available, using in-memory fallback');
  }

  // Initialize new session
  const newSession: SessionData = {
    sessionId: generateUUID(),
    startTime: Date.now(),
    windowStartTime: Date.now(),
    impressionCount: 0,
    activeRefreshableAds: [],
  };

  // Cache in memory
  inMemorySession = newSession;

  logger.log('New session created', {
    sessionId: newSession.sessionId,
  });

  if (chrome.storage?.session) {
    await chrome.storage.session.set({ [SESSION_STORAGE_KEY]: newSession });
  } else {
    logger.log('Failed to persist new session (storage unavailable)');
  }

  return newSession;
}

/**
 * Increments the session impression counter and tracks active refreshable ads.
 *
 * @param payload.adId Identifier for the ad being counted.
 * @param payload.maxImpressions Optional per-session cap; defaults to {@link DEFAULT_MAX_IMPRESSIONS}.
 * @param payload.sessionWindowMs Optional window duration in ms; defaults to {@link DEFAULT_SESSION_WINDOW_MS}.
 * @returns Object with allowance flag, remaining quota, and total impressions.
 */
async function updateSessionImpression(payload: {
  adId: string;
  maxImpressions?: number;
  sessionWindowMs?: number;
}): Promise<{ allowed: boolean; remaining: number; total: number }> {
  const maxImpressions = payload.maxImpressions ?? DEFAULT_MAX_IMPRESSIONS;
  const sessionWindowMs = payload.sessionWindowMs ?? DEFAULT_SESSION_WINDOW_MS;
  const session = await getSessionData();

  const now = Date.now();
  const windowStartTime = session.windowStartTime ?? session.startTime;
  const windowExpired = now - windowStartTime >= sessionWindowMs;

  const currentCount = windowExpired ? 0 : session.impressionCount;
  const newCount = currentCount + 1;
  const allowed = newCount <= maxImpressions;
  const remaining = Math.max(0, maxImpressions - newCount);

  logger.log('Session impression check', {
    adId: payload.adId,
    currentCount,
    newCount,
    maxImpressions,
    sessionWindowMs,
    allowed,
    remaining,
    sessionId: session.sessionId,
    windowExpired,
  });

  if (allowed) {
    const updatedSession: SessionData = {
      ...session,
      windowStartTime: windowExpired ? now : windowStartTime,
      impressionCount: newCount,
      activeRefreshableAds: session.activeRefreshableAds.includes(payload.adId)
        ? session.activeRefreshableAds
        : [...session.activeRefreshableAds, payload.adId],
    };

    // Update in-memory cache
    inMemorySession = updatedSession;

    if (chrome.storage?.session) {
      await chrome.storage.session.set({
        [SESSION_STORAGE_KEY]: updatedSession,
      });
      logger.log('Session updated, impression count:', newCount);
    } else {
      // Session storage not available
      logger.log(
        'Session updated in memory only (storage unavailable), count:',
        newCount
      );
    }
  } else {
    logger.log('Max impressions reached, blocking ad display');
  }

  return { allowed, remaining, total: newCount };
}

/**
 * Stops a refreshable ad by clearing its timer and removing it from session tracking.
 *
 * @param payload.adId Identifier of the refreshable ad to stop.
 */
async function stopRefreshableAd(payload: { adId: string }): Promise<void> {
  const { adId } = payload;

  // Clear the timer if it exists in this worker lifetime.
  // After a service worker restart the Map is empty, which is expected —
  // the actual refresh timers live in the UI context (popup/sidepanel),
  // not in the background.
  const ad = activeRefreshableAds.get(adId);
  if (ad) {
    clearInterval(ad.timerId);
    activeRefreshableAds.delete(adId);
  }

  // Remove from session tracking
  const session = await getSessionData();
  const updatedActiveAds = session.activeRefreshableAds.filter(
    (id) => id !== adId
  );

  const updatedSession: SessionData = {
    ...session,
    activeRefreshableAds: updatedActiveAds,
  };

  // Sync in-memory cache so subsequent reads within this worker lifetime
  // see the updated list without hitting storage again
  inMemorySession = updatedSession;

  if (chrome.storage?.session) {
    await chrome.storage.session.set({
      [SESSION_STORAGE_KEY]: updatedSession,
    });
  }
}
